import type { ReactNode } from 'react';

/**
 * The opening of every screen: the landing page's `PageHero` recipe — gradient wash, eyebrow, H1,
 * lead — so the ops tool reads as the same product as easy-invoices.de and the Steuerberater portal.
 *
 * Compared with `apps/portal`'s copy of this component the padding is roughly halved. The portal's
 * hero opens a page someone reads once; this one opens a queue an operator refreshes all day, and
 * the vertical space a marketing hero spends is space the first row of data does not get.
 *
 * The horizontal gutters are the landing page's (`px-4 sm:px-6 lg:px-8`) but WITHOUT its
 * `max-w-7xl` — `container-custom` would centre a queue table inside the space left over next to
 * the sidebar, and the columns that get squeezed out are the ones an operator came for.
 *
 * `actions` is the right-hand slot for the buttons that act on the whole page (pause a queue,
 * retry everything). `children` sits under the lead for pills, counters and back links.
 */
export function PageHead({
  eyebrow,
  title,
  lead,
  actions,
  children,
}: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly lead?: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <section className="bg-gradient-to-b from-primary-50/70 via-white to-white border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight tracking-tight break-words">
              {title}
            </h1>
            {lead && (
              <p className="mt-2 text-sm md:text-base text-gray-600 leading-relaxed max-w-3xl">
                {lead}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex flex-wrap gap-2 lg:justify-end shrink-0">
              {actions}
            </div>
          )}
        </div>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </section>
  );
}

/**
 * The body below a {@link PageHead}. `narrow` constrains reading-width content such as the
 * backfill and broadcast forms, which are prose-and-fields rather than data.
 */
export function PageBody({
  narrow,
  children,
}: {
  readonly narrow?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 md:py-8">
      {narrow ? (
        <div className="max-w-3xl space-y-5">{children}</div>
      ) : (
        <div className="space-y-5">{children}</div>
      )}
    </div>
  );
}
